<script setup lang="ts">
import { XMark, Download, Bookmark, Share, Lock } from "./icons/index.ts";
import { useContentStore } from "@/stores/contentStore.ts";
import { useAuthStore } from "@/stores/authStore.ts";
import { storeToRefs } from "pinia";
import ProgressBar from "./ProgressBar.vue";
import { useEditionProgress } from "@/lib/edition.ts";
import { config } from "@/lib/variantConfig.ts";
import EditionArticles from "./editions/EditionArticles.vue";
import LoadingIndicator from "./general/LoadingIndicator.vue";
import { computed, onMounted, ref } from "vue";
import { pushAppEvents } from "@/repository/index.ts";
import { AppEvent } from "@/types/appEvents.ts";
import { notify } from "@/lib/notify.ts";
import ShareDialog from "./ShareDialog.vue";
import { useQueryClient } from "@tanstack/vue-query";
import { useI18n } from "vue-i18n";
import { AnalyticsEvent, useAnalytics } from "@/lib/analytics.ts";
import { DialogClose, DialogDescription, DialogRoot, DialogTitle, DialogTrigger } from "reka-ui";
import DialogContent from "./DialogContent.vue";

const loadingBookmark = ref(false);
const localBookmark = ref<boolean>();

const { t } = useI18n();
const { logEvent } = useAnalytics();
const queryClient = useQueryClient();

const authStore = useAuthStore();
const contentStore = useContentStore();
const { selectedEdition: edition, selectedEditionPage } = storeToRefs(contentStore);

const { hasProgress, progress } = useEditionProgress(edition);

const editionLink = computed(() => {
  if (!edition.value) return "";
  const page = selectedEditionPage.value;
  return page ? `/edition/${edition.value.id}?page=${page}` : `/edition/${edition.value.id}`;
});

const isBookmarked = computed(() => {
  if (localBookmark.value !== undefined) return localBookmark.value;
  if (!edition.value?.end_user_edition_data) return false;
  return edition.value.end_user_edition_data?.bookmarked ?? false;
});

const hasAccess = computed(() => {
  return edition.value?.end_user_subscription?.has_access;
});

const resetEditionQueries = () => {
  queryClient.invalidateQueries({
    queryKey: ["recent-editions"],
  });
  queryClient.invalidateQueries({
    queryKey: ["continue-reading-editions"],
  });
  queryClient.invalidateQueries({
    queryKey: ["saved", "magazines"],
  });
};

const toggleBookmark = async () => {
  if (!edition.value || loadingBookmark.value) return;
  loadingBookmark.value = true;
  try {
    await pushAppEvents([
      {
        type: AppEvent.EditionBookmarkModified,
        data: {
          edition_id: edition.value.id,
          bookmarked: !isBookmarked.value,
          bookmark_modified_at: new Date().toISOString(),
        },
      },
    ]);
    localBookmark.value = !isBookmarked.value;
    resetEditionQueries();
    logEvent(AnalyticsEvent.ContentBookmark, {
      contentId: edition.value.id,
      contentType: "edition",
      contentName: edition.value.name,
    });
  } catch (err) {
    notify.error({
      title: t("common.error.title"),
      text: t("common.error.description"),
    });
  } finally {
    loadingBookmark.value = false;
  }
};

const downloadPdf = () => {
  contentStore.downloadSelectedEdition = contentStore.selectedEdition;
};

onMounted(() => {
  if (edition.value) {
    logEvent(AnalyticsEvent.ContentPreview, {
      contentId: edition.value.id,
      contentType: "edition",
      contentName: edition.value.name,
    });
  }
});

const handleSignInClick = () => {
  contentStore.selectedEdition = undefined;
  contentStore.selectedEditionPage = undefined;
  authStore.showLoginModal = true;
};
</script>

<template>
  <div v-if="edition" class="overflow-hidden max-h-[38rem] w-full rounded-xl">
    <DialogClose class="btn-round-white absolute top-6 right-6 z-20">
      <XMark />
    </DialogClose>
    <div class="relative overflow-auto h-full" id="edition-modal">
      <div
        class="bg-background-backdrop absolute h-40 w-full top-0 left-0 -z-10"
      />
      <div class="px-6 pt-6 flex">
        <img
          class="shadow-lg mb-2 bg-background-tertiary object-cover w-32"
          :class="{
            'grayscale opacity-60': !edition.end_user_subscription?.has_access,
          }"
          :style="{
            aspectRatio: config.thumbnail_aspect_ratio,
          }"
          :src="edition.thumbnail_url"
          :alt="edition.name"
        />
        <div class="flex-1 ml-6 pt-14">
          <h2 class="text-xl font-bold mb-2">{{ edition.name }}</h2>
          <ProgressBar v-if="hasProgress" :progress="progress" />
        </div>
      </div>
      <div class="px-6 pb-6 mt-4">
        <div v-if="hasAccess">
          <RouterLink class="btn-primary w-full" :to="editionLink">
            {{
              hasProgress
                ? $t("edition_modal.continue_reading_button")
                : $t("edition_modal.open_button")
            }}
          </RouterLink>
          <div class="flex flex-col md:flex-row gap-3 mt-3">
            <button
              v-if="config.allow_downloads"
              class="btn-white flex-1"
              :class="{ 'btn--border': hasAccess }"
              :disabled="!hasAccess"
              @click="downloadPdf"
            >
              <Download size="20" class="mr-1" />
              <span>{{ $t("edition_modal.download_button") }}</span>
            </button>
            <button
              class="btn-white flex-1"
              :class="{ 'btn--border': hasAccess }"
              :disabled="!hasAccess"
              @click="toggleBookmark"
            >
              <LoadingIndicator v-if="loadingBookmark" size="sm" />
              <Bookmark
                v-else
                size="20"
                class="mr-1"
                :weight="isBookmarked ? 'fill' : 'regular'"
              />
              <span>{{
                isBookmarked
                  ? $t("edition_modal.bookmark_remove")
                  : $t("edition_modal.bookmark_add")
              }}</span>
            </button>
            <DialogRoot>
              <DialogTrigger
                class="btn-white flex-1"
                :class="{ 'btn--border': hasAccess }"
                :disabled="!hasAccess"
              >
                <Share size="20" class="mr-1" />
                <span>{{ $t("edition_modal.share_button") }}</span>
              </DialogTrigger>
              <DialogContent>
                <ShareDialog type="edition" :edition="edition" />
              </DialogContent>
            </DialogRoot>
          </div>
        </div>
        <div
          v-else
          class="bg-background-secondary p-3 gap-2 flex items-center rounded-xl"
        >
          <div class="mb-auto">
            <Lock weight="fill" size="24" class="text-icon-primary" />
          </div>
          <div>
            <DialogTitle class="text-text-primary font-bold">
              {{ $t("edition_modal.no_access.title") }}
            </DialogTitle>
            <DialogDescription class="text-text-secondary">
              {{ $t("edition_modal.no_access.description") }}
            </DialogDescription>
          </div>
          <button
            @click="handleSignInClick"
            class="btn-primary"
            v-if="!authStore.user"
          >
            {{ $t("common.sign_in") }}
          </button>
          <a v-else class="btn-primary" :href="config.links.subscription_url">
            {{ $t("common.subscribe") }}
          </a>
        </div>
        <div class="mt-4">
          <h3 class="text-lg font-semibold pb-2 border-b border-border-primary">
            {{ $t("edition_modal.articles_title") }}
          </h3>
          <EditionArticles class="mt-4" :edition="edition" />
        </div>
      </div>
    </div>
  </div>
</template>
